// Global packages or components
import { useLoaderData, redirect } from '@remix-run/react'
import { resolveBlocks } from '~/.server'
import { getPage } from '~/sanity'
import { getFaqBlockSchema, getMeta, getSchema, Schema } from '~/utils'
import { Blocks } from '~/components'
import { usePageInfo } from '~/hooks'

// Type declarations
import type {
  MetaFunction,
  LoaderFunction,
  LoaderFunctionArgs,
} from '@remix-run/node'

// Route loader
export const loader: LoaderFunction = async ({
  params,
  request,
}: LoaderFunctionArgs) => {
  const env = process.env
  const slug = params['*']

  // Issue with URL
  if (!slug) {
    throw new Response('Not Found', {
      status: 404,
    })
  }

  // Sanity fetch
  const data = await getPage({
    dataset: env?.ENVIRONMENT,
    projectId: env?.SANITY_PROJECT,
    slug,
  })

  // Page not found in Sanity
  if (!data) {
    throw new Response('Not Found', {
      status: 404,
    })
  }

  // SEO data
  const seo = data?.seo

  // Custom page level redirect
  if (seo?.redirectPage && seo?.redirect?.type) {
    return redirect(
      seo?.redirect?.page?.slug?.current
        ? '/' + seo.redirect.page.slug.current
        : '/',
      Number(seo.redirect.type)
    )
  }

  // Server renders any blog listing, paged by the page param. After the
  // redirect, so a redirected page never fetches posts
  const { blocks, listing } = await resolveBlocks({
    blocks: data?.flexibleContent,
    request,
  })

  // Return data
  return Response.json({
    seo: seo,
    title: data?.title,
    blocks,
    listing,
    uspBlock: data?.showUSP,
  })
}

// Route meta
export const meta: MetaFunction = ({ matches, data, location }) => {
  return getMeta({
    matches,
    data,
    location,
  })
}

// Route view
export default function Index() {
  const { blocks, title, seo } = useLoaderData<typeof loader>()

  usePageInfo({
    pageType: 'Page',
    pageTitle: title,
  })

  const faqSchema = getFaqBlockSchema(blocks)

  const jsonLd = [
    getSchema({
      type: 'page',
      page: seo,
    }),
    ...(faqSchema ? [faqSchema] : []),
  ]

  return (
    <main>
      <Schema data={jsonLd} />
      {blocks && <Blocks blocks={blocks} />}
    </main>
  )
}
